import { hasScreencasts, hasScreencastsAccessLevel } from 'src/app/common/menu-def.provider';
import { AuthCompany, Screencast, ScreencastScreen, User } from 'src/models';

const averageThresholds = {
  keys: 120,
  movements: 120,
  clicks: 120,
};

export function getScreenActivityAverage(item: ScreencastScreen, type: (keyof typeof averageThresholds)): number {
  if (item.meta.period === 0) { return 0; }
  return Math.min(item.meta[type] / item.meta.period * 60 / averageThresholds[type], 1) || 0;
}

/**
 * Newest-first ordering, matching the Screencasts page default (`sort: '_date'`).
 * Used for endpoints that return screencasts in no guaranteed order.
 */
export function sortScreencastsByDateDesc(items: Screencast[]): Screencast[] {
  return [...(items || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

/**
 * Whether the viewer may see screencasts of a specific user. On top of the
 * company-level `hasScreencasts` check (which only looks at the viewer's own
 * settings), the viewer needs the screencasts access level (or must be looking
 * at themselves) and the viewed user must have screenshots or videos capture
 * enabled. Mirrors the gating of the user-details / unusual-activity panels.
 * Returns false while `user` is not loaded yet.
 */
export function canViewUserScreencasts(company: AuthCompany, authUserId: string, user: User): boolean {
  if (!user) { return false; }
  return hasScreencasts(company)
    && (hasScreencastsAccessLevel(company) || authUserId === user.id)
    && !(user.screenshots === 0 && user.videos === 'off');
}

export interface ScreenshotGroup {
  screens: ScreencastScreen[];
  isMultiScreen: boolean;
}

/**
 * Creates a grouped representation of screenshots for rendering merged views
 */
export function buildMergedScreens(screens: ScreencastScreen[]): ScreenshotGroup {
  return {
    screens,
    isMultiScreen: screens.length > 1,
  };
}
